import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { Link } from 'react-router-dom';
import {
  FileText,
  CheckCircle,
  TrendingUp,
  Search,
  MoreVertical,
  Calendar,
  ShoppingBag,
  Upload,
  Scale,
} from 'lucide-react';

import {
  claimResponseToEditForm,
  ExportRefundClaimDetailResponse,
  ExportRefundClaimResponse,
  ExportRefundDocumentResponse,
} from '../exportRefund';

interface ExportRefundDocumentView extends ExportRefundDocumentResponse {
  preview_url?: string;
}

type AdminExportRefundClaim = Omit<ExportRefundClaimDetailResponse, 'documents'> & {
  documents: ExportRefundDocumentView[];
};

const statusColors: Record<string, string> = {
  draft: '#64748B',
  processing: '#F59E0B',
  error: '#EF4444',
  refunded: '#10B981',
  submitted: '#2563EB',
  under_review: '#8B5CF6',
  approved: '#10B981',
  rejected: '#EF4444',
  paid: '#10B981',
};

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [claims, setClaims] = useState<AdminExportRefundClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ReturnType<typeof claimResponseToEditForm> | null>(null);
  const [saving, setSaving] = useState(false);
  const [freezingId, setFreezingId] = useState<string | null>(null);

  const loadClaimDetails = useCallback(async (baseClaims: ExportRefundClaimResponse[]) => {
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    const detailedClaims = await Promise.all(
      baseClaims.map(async (claim) => {
        const response = await axios.get(API.getExportRefundClaimDetail(claim.user_id, claim.id), { headers });
        const documents = await Promise.all(
          response.data.documents.map(async (document: ExportRefundDocumentResponse) => {
            if (!document.content_type.startsWith('image/')) {
              return document;
            }
            const blobResponse = await axios.get(API.downloadExportRefundDocument(claim.user_id, document.id), {
              headers,
              responseType: 'blob',
            });
            return {
              ...document,
              preview_url: window.URL.createObjectURL(blobResponse.data),
            };
          }),
        );
        return { ...response.data, documents } as AdminExportRefundClaim;
      }),
    );
    setClaims(detailedClaims);
  }, []);

  const fetchAllClaims = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const response = await axios.get(API.listAllExportRefundClaims(), { headers });
      await loadClaimDetails(response.data as ExportRefundClaimResponse[]);
    } catch (loadError) {
      console.error('Failed to load admin data:', loadError);
      setError('Failed to load all refund claims. Admin privileges required.');
    } finally {
      setLoading(false);
    }
  }, [loadClaimDetails]);

  useEffect(() => {
    fetchAllClaims();
  }, [fetchAllClaims]);

  const handleEdit = (claim: AdminExportRefundClaim) => {
    setEditingId(claim.id);
    setEditForm(claimResponseToEditForm(claim));
  };

  const handleSave = async (userId: string, claimId: string) => {
    if (!editForm) return;
    setSaving(true);
    setError('');
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    const payload = {
      progress_status: editForm.progress_status,
      status_reason: editForm.progress_status === 'error' ? editForm.status_reason : undefined,
      transaction_date: editForm.transaction_date,
      shop_name: editForm.shop_name,
      brand_name: editForm.brand_name,
      total_amount: editForm.total_amount,
      vat_amount: editForm.vat_amount,
      price_without_vat: editForm.price_without_vat,
      payment_method: editForm.payment_method,
      item_description: editForm.item_description,
    };

    try {
      await axios.put(
        API.adminUpdateExportRefundClaim(claimId),
        payload,
        { headers },
      );
      setEditingId(null);
      await fetchAllClaims();
    } catch (err: any) {
      console.error('Failed to update claim:', err);
      setError(err.response?.data?.detail || 'Failed to update claim.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const [confirmingShippingId, setConfirmingShippingId] = useState<string | null>(null);

  const handleConfirmShippingBankTransfer = async (claimId: string) => {
    if (!window.confirm('Confirm that this claim\'s shipping-fee bank transfer has arrived?')) {
      return;
    }
    setConfirmingShippingId(claimId);
    setError('');
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(
        API.confirmClaimShippingBankTransfer(claimId),
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchAllClaims();
    } catch (err: any) {
      console.error('Failed to confirm shipping bank transfer:', err);
      setError(err.response?.data?.detail || 'Failed to confirm shipping bank transfer.');
    } finally {
      setConfirmingShippingId(null);
    }
  };

  // BDD 4-A line 242-245: ops uploads the logistics label / Commercial
  // Invoice to the customer's claim. The backend route already supported
  // admin-on-behalf-of-customer uploads (todo.txt section 6.3/8.5), but
  // there was no button anywhere in the app to actually do it (todo.txt 18.2).
  const [uploadDocClaim, setUploadDocClaim] = useState<AdminExportRefundClaim | null>(null);
  const [uploadDocType, setUploadDocType] = useState<'logistics_label' | 'commercial_invoice'>('logistics_label');
  const [uploadDocFile, setUploadDocFile] = useState<File | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const submitDocumentUpload = async () => {
    if (!uploadDocClaim || !uploadDocFile) return;
    setUploadingDoc(true);
    setError('');
    setMessage('');
    const token = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('document_type', uploadDocType);
    formData.append('file', uploadDocFile);
    try {
      await axios.post(API.uploadExportRefundDocument(uploadDocClaim.user_id, uploadDocClaim.id), formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setMessage(`${uploadDocType === 'logistics_label' ? 'Logistics label' : 'Commercial Invoice'} uploaded for ${uploadDocClaim.reference_number}.`);
      setUploadDocClaim(null);
      setUploadDocFile(null);
      await fetchAllClaims();
    } catch (err: any) {
      console.error('Failed to upload document:', err);
      setError(err.response?.data?.detail || 'Failed to upload document.');
    } finally {
      setUploadingDoc(false);
    }
  };

  // BDD 4-A line 240-241: true-up the actual vs. estimated shipping fee
  // against the customer's Reward balance once ops has the real carrier
  // invoice. Backend endpoint existed and was tested, but had no UI
  // anywhere (todo.txt 18.3).
  const [reconcileClaim, setReconcileClaim] = useState<AdminExportRefundClaim | null>(null);
  const [actualShippingFee, setActualShippingFee] = useState('');
  const [reconciling, setReconciling] = useState(false);

  const submitReconcile = async () => {
    if (!reconcileClaim || !actualShippingFee) return;
    setReconciling(true);
    setError('');
    setMessage('');
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.post(
        API.reconcileClaimShippingFee(reconcileClaim.id),
        { actual_shipping_fee: Number(actualShippingFee) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { adjustment_amount, new_reward_balance } = response.data;
      setMessage(
        `Shipping fee reconciled for ${reconcileClaim.reference_number}: ` +
        `${adjustment_amount >= 0 ? 'deducted' : 'credited'} £${Math.abs(adjustment_amount).toFixed(2)} ` +
        `(new reward balance £${new_reward_balance.toFixed(2)}).`,
      );
      setReconcileClaim(null);
      setActualShippingFee('');
      await fetchAllClaims();
    } catch (err: any) {
      console.error('Failed to reconcile shipping fee:', err);
      setError(err.response?.data?.detail || 'Failed to reconcile shipping fee.');
    } finally {
      setReconciling(false);
    }
  };

  const handleFreezeViolation = async (claimId: string) => {
    if (!window.confirm('Freeze this partner\'s card and apply a 20% penalty for a compliance violation?')) {
      return;
    }
    setFreezingId(claimId);
    setError('');
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(
        API.freezeExportRefundClaimViolation(claimId),
        { reason: 'No qualified invoice or receipt was submitted by the compliance deadline' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchAllClaims();
    } catch (err: any) {
      console.error('Failed to freeze card for violation:', err);
      setError(err.response?.data?.detail || 'Failed to freeze card for violation.');
    } finally {
      setFreezingId(null);
    }
  };

  const stats = useMemo(() => {
    return {
      totalClaims: claims.length,
      pendingClaims: claims.filter(c => ['submitted', 'processing', 'under_review'].includes(c.status.progress_status)).length,
      refundedClaims: claims.filter(c => ['refunded', 'paid', 'approved'].includes(c.status.progress_status)).length,
      totalValue: claims.reduce((acc, c) => acc + (c.amounts.total_amount || 0), 0),
    };
  }, [claims]);

  if (!user || !user.is_admin) return <div>Access Denied</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Admin Dashboard</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-outline" onClick={fetchAllClaims}>Refresh</button>
          <button className="btn btn-primary" style={{ background: '#DC2626' }}>Download Report</button>
        </div>
      </div>

      {(error || message) && (
        <div style={{
          marginBottom: '24px', padding: '16px', borderRadius: '8px', fontSize: '14px',
          background: error ? '#FEF2F2' : '#F0FDF4',
          color: error ? '#991B1B' : '#166534',
          border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`,
        }}>
          {error || message}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginBottom: '40px' }}>
        <div className="section-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '14px', color: '#64748B', fontWeight: 500, marginBottom: '8px' }}>Total Claims</div>
              <div style={{ fontSize: '28px', fontWeight: 700 }}>{stats.totalClaims}</div>
            </div>
            <div style={{ padding: '8px', background: '#F1F5F9', color: '#475569', borderRadius: '8px' }}>
              <FileText size={20} />
            </div>
          </div>
        </div>

        <div className="section-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '14px', color: '#64748B', fontWeight: 500, marginBottom: '8px' }}>Pending Review</div>
              <div style={{ fontSize: '28px', fontWeight: 700 }}>{stats.pendingClaims}</div>
            </div>
            <div style={{ padding: '8px', background: '#FEF9C3', color: '#854D0E', borderRadius: '8px' }}>
              <TrendingUp size={20} />
            </div>
          </div>
        </div>

        <div className="section-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '14px', color: '#64748B', fontWeight: 500, marginBottom: '8px' }}>Total Refunded</div>
              <div style={{ fontSize: '28px', fontWeight: 700 }}>{stats.refundedClaims}</div>
            </div>
            <div style={{ padding: '8px', background: '#DCFCE7', color: '#166534', borderRadius: '8px' }}>
              <CheckCircle size={20} />
            </div>
          </div>
        </div>

        <div className="section-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '14px', color: '#64748B', fontWeight: 500, marginBottom: '8px' }}>Total Value</div>
              <div style={{ fontSize: '28px', fontWeight: 700 }}>£{stats.totalValue.toLocaleString()}</div>
            </div>
            <div style={{ padding: '8px', background: '#EFF6FF', color: '#2563EB', borderRadius: '8px' }}>
              <ShoppingBag size={20} />
            </div>
          </div>
        </div>
      </div>

      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '24px', borderBottom: '1px solid var(--sidebar-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title" style={{ margin: 0 }}>Recent Claims</h2>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input className="form-input" style={{ paddingLeft: '40px', width: '300px' }} placeholder="Search claims..." />
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--sidebar-border)' }}>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Reference</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>User</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Date</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Amount</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Status</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Compliance</th>
              <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center' }}>Loading...</td></tr>
            ) : claims.map((claim) => (
              <tr key={claim.id} style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
                <td style={{ padding: '20px 24px' }}>
                  <div style={{ fontWeight: 600, color: '#0F172A' }}>{claim.reference_number}</div>
                  <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>{claim.purchase.shop_name}</div>
                </td>
                <td style={{ padding: '20px 24px' }}>
                  <Link to={`/admin/users/${claim.user_id}`} style={{ color: '#2563EB', textDecoration: 'none', fontWeight: 500 }}>
                    {claim.user_id.split('-')[0]}...
                  </Link>
                </td>
                <td style={{ padding: '20px 24px', color: '#64748B' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={14} />
                    {claim.purchase.transaction_date}
                  </div>
                </td>
                <td style={{ padding: '20px 24px', fontWeight: 600 }}>
                  £{(claim.amounts.total_amount ?? 0).toFixed(2)}
                </td>
                <td style={{ padding: '20px 24px' }}>
                  <span className="status-pill" style={{ 
                    background: `${statusColors[claim.status.progress_status]}15`, 
                    color: statusColors[claim.status.progress_status],
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    width: 'fit-content'
                  }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }}></div>
                    {claim.status.progress_status.toUpperCase().replace('_', ' ')}
                  </span>
                </td>
                <td style={{ padding: '20px 24px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                    {claim.status.compliance_violation ? (
                      <span className="status-pill status-cancelled">VIOLATION -£{claim.status.penalty_fee_amount.toFixed(2)}</span>
                    ) : claim.status.late_penalty_applied ? (
                      <span className="status-pill status-pending">LATE -£{claim.status.penalty_fee_amount.toFixed(2)}</span>
                    ) : claim.status.document_type ? (
                      <span className="status-pill status-active">ON TIME</span>
                    ) : (
                      <span className="status-pill status-outline">PENDING DOCS</span>
                    )}
                    {claim.status.reward_credited && (
                      <span className="status-pill status-active">CASHBACK +£{(claim.status.reward_credited_amount ?? 0).toFixed(2)}</span>
                    )}
                    {claim.status.settlement_available_at && !claim.status.reward_credited && !claim.status.compliance_violation && (
                      <span
                        className="status-pill status-pending"
                        title="Approving now succeeds but withholds cashback until this date — re-approve after it passes to credit it"
                      >
                        SETTLES {new Date(claim.status.settlement_available_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button className="btn btn-outline" style={{ padding: '8px' }} onClick={() => handleEdit(claim)}>
                      Edit
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: '8px' }}
                      onClick={() => { setUploadDocClaim(claim); setUploadDocType('logistics_label'); setUploadDocFile(null); }}
                      title="Upload logistics label / Commercial Invoice"
                    >
                      <Upload size={16} />
                    </button>
                    {claim.status.shipping_estimated_fee != null && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px' }}
                        onClick={() => { setReconcileClaim(claim); setActualShippingFee(''); }}
                        title="Reconcile actual vs. estimated shipping fee"
                      >
                        <Scale size={16} />
                      </button>
                    )}
                    {claim.status.shipping_payment_method === 'bank_transfer' && !claim.status.shipping_fee_paid && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px' }}
                        onClick={() => handleConfirmShippingBankTransfer(claim.id)}
                        disabled={confirmingShippingId === claim.id}
                      >
                        {confirmingShippingId === claim.id ? 'Confirming...' : 'Confirm Shipping Transfer'}
                      </button>
                    )}
                    {!claim.status.compliance_violation && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px', color: '#DC2626', borderColor: '#FCA5A5' }}
                        onClick={() => handleFreezeViolation(claim.id)}
                        disabled={freezingId === claim.id}
                      >
                        {freezingId === claim.id ? 'Freezing...' : 'Freeze (Violation)'}
                      </button>
                    )}
                    <button className="btn btn-outline" style={{ padding: '8px' }}>
                      <MoreVertical size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {editingId && editForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '600px', margin: '20px' }}>
            <h2 className="card-title">Edit Claim: {claims.find(c => c.id === editingId)?.reference_number}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-input" value={editForm.progress_status} onChange={(e) => setEditForm({...editForm, progress_status: e.target.value})}>
                  {Object.keys(statusColors).map(status => (
                    <option key={status} value={status}>{status.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Amount (£)</label>
                <input className="form-input" type="number" value={editForm.total_amount} onChange={(e) => setEditForm({...editForm, total_amount: Number(e.target.value)})} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={handleCancel}>Cancel</button>
              <button className="btn btn-primary" onClick={() => handleSave(claims.find(c => c.id === editingId)!.user_id, editingId)} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadDocClaim && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h2 className="card-title">Upload Shipping Document</h2>
            <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-8px', marginBottom: '16px' }}>
              {uploadDocClaim.reference_number} — uploads here notify the customer by email.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="upload-doc-type-select">Document Type</label>
              <select
                id="upload-doc-type-select"
                className="form-input"
                value={uploadDocType}
                onChange={(e) => setUploadDocType(e.target.value as 'logistics_label' | 'commercial_invoice')}
              >
                <option value="logistics_label">Logistics Label (物流面單)</option>
                <option value="commercial_invoice">Commercial Invoice</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="upload-doc-file-input">File</label>
              <input
                id="upload-doc-file-input"
                className="form-input"
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => setUploadDocFile(e.target.files?.[0] || null)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setUploadDocClaim(null)} disabled={uploadingDoc}>Cancel</button>
              <button className="btn btn-primary" onClick={submitDocumentUpload} disabled={uploadingDoc || !uploadDocFile}>
                {uploadingDoc ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reconcileClaim && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h2 className="card-title">Reconcile Shipping Fee</h2>
            <p style={{ fontSize: '13px', color: '#64748B', marginTop: '-8px', marginBottom: '16px' }}>
              {reconcileClaim.reference_number} — estimated shipping fee was
              £{(reconcileClaim.status.shipping_estimated_fee ?? 0).toFixed(2)}.
              Enter the real carrier-invoiced amount; the difference is deducted from or credited to the customer's Reward balance.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="actual-shipping-fee-input">Actual Shipping Fee (£)</label>
              <input
                id="actual-shipping-fee-input"
                className="form-input"
                type="number"
                step="0.01"
                value={actualShippingFee}
                onChange={(e) => setActualShippingFee(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setReconcileClaim(null)} disabled={reconciling}>Cancel</button>
              <button className="btn btn-primary" onClick={submitReconcile} disabled={reconciling || !actualShippingFee}>
                {reconciling ? 'Reconciling...' : 'Reconcile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
